import { debounce, throttle } from 'lodash';
import { ReactNode, useEffect, useRef, useState } from 'react';

type FixedElementOnScrollPropsType = {
    /** The content to be rendered within the fixed element */
    children?: ReactNode | undefined;
    /** The interval in milliseconds to throttle scroll events (default: 16ms for ~60fps) */
    throttleInterval?: number | undefined;
    /** Whether to enable smooth transitions when the element's position changes (default: true) */
    transitionEnabled?: boolean | undefined;
};

/**
 * A component that creates a fixed-position element that smoothly follows page scroll.
 * The element maintains its position relative to the viewport, confined to the parent, while accounting for a header
 *
 * @example
 * ```tsx
 * <FixedElementOnScroll throttleInterval={20}>
 *   <div>This content will follow scroll</div>
 * </FixedElementOnScroll>
 * ```
 *
 * @param props - Component props
 * @param props.children - Content to be rendered within the fixed element
 * @param props.throttleInterval - Interval in milliseconds to throttle scroll events (default: 16ms for ~60fps)
 * @param props.transitionEnabled - Whether to enable smooth transitions when the element's position changes (default: true)
 */
const FixedElementOnScroll = ({
    children,
    throttleInterval = 16,
    transitionEnabled = true,
}: FixedElementOnScrollPropsType) => {
    // Track vertical translation of the element
    const [translateY, setTranslateY] = useState(0);
    // Reference to the DOM element for position calculations
    const elementRef = useRef<HTMLDivElement>(null);
    // Height of the fixed header in pixels
    const headerHeight = 60;

    /**
     * Gets the initial vertical offset of the element from the top of its container
     * @returns The offset in pixels
     */
    const updateInitialOffsetTop = () => {
        const element = elementRef.current;
        return element?.offsetTop || 0;
    };

    useEffect(() => {
        // Store initial position of the element
        let initialOffsetTop = updateInitialOffsetTop();

        /**
         * Handles scroll events by updating the element's position
         * Throttled for performance optimization
         */
        const handleScroll = throttle(() => {
            const parentHeight = elementRef.current?.parentElement?.offsetHeight;
            if (!parentHeight) {
                return;
            }

            const scrollY = window.scrollY;
            if (scrollY < initialOffsetTop) {
                setTranslateY(0);
                return;
            }

            // Calculate new vertical translation, accounting for header height
            const rawTranslateY = scrollY + headerHeight - initialOffsetTop;

            // Calculate the maximum allowed translation before hitting parent bottom
            const parentBottom = parentHeight - elementRef.current.offsetHeight - headerHeight;
            const maxTranslateY = Math.max(0, parentBottom);

            // Limit the translation to keep the element within parent bounds
            const boundedTranslateY = Math.min(rawTranslateY, maxTranslateY);

            if (boundedTranslateY !== translateY) {
                setTranslateY(boundedTranslateY);
            }
        }, throttleInterval);

        /**
         * Handles window resize events by updating the element's initial position
         * Debounced to prevent excessive calculations during resize
         */
        const handleResize = debounce(() => {
            initialOffsetTop = updateInitialOffsetTop();
        }, 200);

        // Attach event listeners
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);

        // Initial position calculation
        handleScroll();

        // Cleanup event listeners on component unmount
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [throttleInterval, translateY]);

    return (
        <div
            ref={elementRef}
            style={{
                transform: `translateY(${translateY}px)`,
                position: 'relative',
                zIndex: 2,
                willChange: 'transform', // Optimize for animations
                transition: transitionEnabled ? 'all 0.2s ease-in-out' : 'none', // Smooth transitions
            }}
        >
            {children}
        </div>
    );
};

export default FixedElementOnScroll;
